import * as React from 'react';
import { useEffect } from 'react';

import classNames from 'classnames';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { setLocation } from 'js/lib/coursera.redirect';
import { useRetracked } from 'js/lib/retracked';

import { Link } from '@coursera/cds-core';

import EnrollmentChoiceTypes from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import {
  canEnrollThroughFreemium,
  isFreemiumModelEnabled,
  previewSurfacePreviewLink,
  useFreemiumTracking,
} from 'bundles/enroll-common/utils/freemiumModelUtils';
import { useCourseEnrollModalData } from 'bundles/enroll-modal/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';

import _t from 'i18n!nls/enroll';

import 'css!./__styles__/SubscriptionFooter';

type PropsFromCaller = {
  removeStyles?: boolean;
  message?: string;
};

const S12nSubscriptionFooter = ({ removeStyles, message }: PropsFromCaller) => {
  const { trackEnrollModalContinue } = useEnrollModalEventing();
  const { course, courseTypeMetadataWithVersion, enrollmentAvailableChoices } = useCourseEnrollModalData();
  const { canAuditCourse } = enrollmentAvailableChoices;
  const track = useRetracked();
  const { trackPreviewLinkClick, trackPreviewLinkView } = useFreemiumTracking();

  const isEnrollingThroughFreemium = canEnrollThroughFreemium({
    courseId: course.id,
    enrollmentAvailableChoices,
    courseTypeMetadataWithVersion,
  });

  useEffect(() => {
    if (isEnrollingThroughFreemium) {
      trackPreviewLinkView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnrollingThroughFreemium]);

  if (!canAuditCourse) {
    return null;
  }

  const surfacePreviewLinkVariant = previewSurfacePreviewLink(
    enrollmentAvailableChoices,
    course.id,
    courseTypeMetadataWithVersion
  );
  if (surfacePreviewLinkVariant === 'variant4') {
    return null;
  }

  const redirectToCourseHome = () => {
    const courseSlug = course?.slug;

    if (courseSlug) {
      return setLocation(`/learn/${courseSlug}/home/welcome`);
    }
    return setLocation('/');
  };

  const onClickAuditCourse = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (isEnrollingThroughFreemium) {
      trackPreviewLinkClick();
    }

    track({
      trackingName: 'enroll_subscribe_audit',
      action: 'click',
      trackingData: {
        courseId: course?.id,
      },
    });

    const enrollmentChoiceType = EnrollmentChoiceTypes.AUDIT_COURSE;
    trackEnrollModalContinue(enrollmentChoiceType);
    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[enrollmentChoiceType];
    const options = {
      courseId: course.id,
      isFreemiumLearner: isEnrollingThroughFreemium,
    };

    return submitEnrollmentPromise({ handleSubmitPromise, options }).then(redirectToCourseHome);
  };

  const footerClasses = classNames('rc-SubscriptionFooter', {
    subscriptionFooter: !removeStyles,
  });

  return (
    <div className={footerClasses} data-testid="subscription-footer">
      <p className="body-1-text">
        <FormattedMessage
          // eslint-disable-next-line no-restricted-syntax
          message={message || _t('{audit} this course')}
          audit={
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link component="button" onClick={onClickAuditCourse} data-testid="subscription-footer-audit-button">
              {isFreemiumModelEnabled(course.id) ? _t('Preview') : _t('Audit')}
            </Link>
          }
        />
      </p>
    </div>
  );
};

export default S12nSubscriptionFooter;
